if (typeof CSSSlider !== "function") {
  class CSSSlider extends HTMLElement {
    constructor() {
      super();
      const ua = navigator.userAgent.toLowerCase();
      this._ios15 = ua.includes("applewebkit") && ua.includes("version/15");
      this.initSlider();
    }

    destroySlider() {
      this.classList.remove("enabled");
      this.sliderEnabled = false;
      window.removeEventListener("resize", this.RESIZE_EVENT);
      window.removeEventListener("scroll", this.SCROLL_EVENT);

      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }
    }

    initSlider() {
      // create custom events

      this._readyEvent = new CustomEvent("ready");
      this._changeEvent = new CustomEvent("change");
      this._scrollEvent = new CustomEvent("scroll");
      this._navEvent = new CustomEvent("navigation");
      this._pointerDownEvent = new CustomEvent("pointerDown");
      this._pointerUpEvent = new CustomEvent("pointerUp");

      // setup variables

      this.items = this.querySelectorAll(".css-slide[data-index]");
      this.indexedItems = [];
      this.index = 0;
      this.length = this.items.length;
      this.windowWidth = window.innerWidth;
      this.isTransitioning = false;

      this.scrollLeft = 0;

      if (this.length > 1) {
        // add navigation

        this.navigation = document.createElement("div");
        this.navigation.classList = `css-slider-navigation ${this.getAttribute(
          "data-navigation-class"
        )}`;
        this.navigation.style.display = "none";
        this.navigation.innerHTML = `<div>
          <span class="css-slider-navigation__index">0</span> / <span class="css-slider-navigation__length">0</span>
        </div>
        <button class="css-slider-navigation__prev simple-arrow simple-arrow--left disabled">
          <span class="visually-hidden">${KROWN.settings.locales.prev}</span>
          ${KROWN.settings.symbols.arrow}
        </button>
        <button class="css-slider-navigation__next simple-arrow simple-arrow--right">
          <span class="visually-hidden">${KROWN.settings.locales.next}</span>
          ${KROWN.settings.symbols.arrow}
        </button>`;

        if (this.classList.contains("css-slider--auto-height")) {
          this.parentNode.parentNode.insertBefore(
            this.navigation,
            this.parentNode.nextSibling
          );
        } else {
          this.parentNode.insertBefore(this.navigation, this.nextSibling);
        }

        this.indexEl = this.navigation.querySelector(
          ".css-slider-navigation__index"
        );
        this.lengthEl = this.navigation.querySelector(
          ".css-slider-navigation__length"
        );
        this.prevEl = this.navigation.querySelector(
          ".css-slider-navigation__prev"
        );
        this.nextEl = this.navigation.querySelector(
          ".css-slider-navigation__next"
        );
        if (this.hasAttribute("data-loop")) this.prevEl.classList.remove("disabled");

        this.prevEl.addEventListener("click", (e) => {
          if (!this.isTransitioning) {
            this.changeSlide("prev");
          }
        });
        this.nextEl.addEventListener("click", (e) => {
          if (!this.isTransitioning) {
            this.changeSlide("next");
          }
        });

        if (this.classList.contains("css-slider--auto-height")) {
          this.parentNode.style.height = this.items[0].offsetHeight + "px";
        }

        // scroll event for slides check
        this.SCROLL_EVENT = debounce(() => {
          // checking selected index
          if (!this._sliderBlockScroll && !this.isTransitioning) {
            const scrollItems = this.indexedItems.entries();
            const scrollArray = Array.from(scrollItems, (elm) =>
              Math.abs(this.slideX(elm[1]) - this.scrollLeft)
            );
            const scrollDistance = Math.min(...scrollArray);
            const scrollIndex = scrollArray.indexOf(scrollDistance);
            if (scrollIndex != this.index) {
              this.index = scrollIndex;
              this.checkSlide();
              this.dispatchEvent(this._changeEvent);
            }
          }
        }, 10);

        this.addEventListener("scroll", this.SCROLL_EVENT, { passive: true });

        // reinit on resize
        this.RESIZE_EVENT = debounce(() => {
          if (this.windowWidth != window.innerWidth) {
            this.resetSlider();
          }
          this.windowWidth = window.innerWidth;
        }, 100);
        window.addEventListener("resize", this.RESIZE_EVENT);
        this.resetSlider(true);

        // mouse events for non touch devices

        if (
          !(
            "ontouchstart" in window ||
            (window.DocumentTouch && document instanceof DocumentTouch)
          )
        ) {
          this.addEventListener("mousedown", (e) => {
            if (
              !e.target.closest(
                "a, button, input, select, textarea, .images-with-text-slider__description, .images-with-text-slider__title"
              ) && !this.isTransitioning
            ) {
              this.mouseX = e.screenX;
              this.classList.add("can-drag");
              this.classList.add("mouse-down");
            }
          });
          this.addEventListener("mouseup", (e) => {
            this.classList.remove("mouse-down");
            this.classList.remove("can-drag");
            this.classList.remove("pointer-events-off");
          });

          this.addEventListener("mouseleave", (e) => {
            this.classList.remove("mouse-down");
            this.classList.remove("can-drag");
            this.classList.remove("pointer-events-off");
          });

          this.addEventListener("mousemove", (e) => {
            if (
              this.classList.contains("can-drag") &&
              !e.target.closest(
                "a, button, input, select, textarea, .images-with-text-slider__description, .images-with-text-slider__title"
              ) &&
              !this.isTransitioning
            ) {
              this.classList.add("pointer-events-off");
              let direction = this.mouseX - e.screenX;
              if (Math.abs(direction) > 1) {
                if (direction > 0) {
                  this.changeSlide("next");
                  this.classList.remove("can-drag");
                } else if (direction < 0) {
                  this.changeSlide("prev");
                  this.classList.remove("can-drag");
                }
              }
            }
          });

          this.querySelectorAll(".css-slides-container").forEach((elm) => {
            elm.addEventListener("mousedown", (e) => {
              if (
                !e.target.closest(
                  "a, button, input, select, textarea, .images-with-text-slider__description, .images-with-text-slider__title"
                ) && !this.isTransitioning
              ) {
                e.preventDefault();
              }
            });
          });
        }
      } else if (this.length == 1) {
        this.classList.add("css-slider--singular");
      } else {
        this.classList.add("css-slider--empty");
      }

      // dispatch ready event

      this.classList.add("enabled");
      this.sliderEnabled = true;
      this.dispatchEvent(this._readyEvent);

      if (this.getAttribute("data-auto-scroll") === "true") {
        this.setupAutoScroll();
      }
    }
    setupAutoScroll() {
      const speed = parseInt(this.getAttribute("data-scroll-speed")) || 5000;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }

      this.autoScrollInterval = setInterval(() => {
        if (
          !this.classList.contains("mouse-down") &&
          !this._sliderBlockScroll &&
          !this.isTransitioning
        ) {
          this.changeSlide("next");
        }
      }, speed);

      // Clean up previous event listeners
      this.removeEventListener("mouseenter", this._pauseScroll);
      this.removeEventListener("mouseleave", this._resumeScroll);

      // Store handler references
      this._pauseScroll = () => {
        if (this.autoScrollInterval) {
          clearInterval(this.autoScrollInterval);
        }
      };

      this._resumeScroll = () => {
        if (this.getAttribute("data-auto-scroll") === "true") {
          this.setupAutoScroll();
        }
      };

      // Add listeners
      this.addEventListener("mouseenter", this._pauseScroll);
      this.addEventListener("mouseleave", this._resumeScroll);

      // Add pause button
      const pauseButton = document.createElement("button");
      pauseButton.className = "css-slider-pause";
      pauseButton.setAttribute("aria-label", "Pause automatic slideshow");
      pauseButton.innerHTML = `<span class="visually-hidden">Pause</span>`;

      pauseButton.addEventListener("click", () => {
        const isPaused = pauseButton.classList.contains("is-paused");
        if (isPaused) {
          this.setupAutoScroll();
          pauseButton.classList.remove("is-paused");
          pauseButton.setAttribute("aria-label", "Pause automatic slideshow");
        } else {
          clearInterval(this.autoScrollInterval);
          pauseButton.classList.add("is-paused");
          pauseButton.setAttribute("aria-label", "Play automatic slideshow");
        }
      });

      // Add to navigation if it exists
      if (this.navigation) {
        this.navigation.appendChild(pauseButton);
      }
    }

    changeSlide(direction, behavior = "smooth") {
      if (this.isTransitioning) return;

      // data-loop: the last slide's next is the first (and back), without passing the middle
      const loop = this.hasAttribute("data-loop");
      let wrapped = false;
      if (direction == "next") {
        if (this.index + 1 >= this.length) {
          if (!loop) return;
          this.index = 0;
          wrapped = true;
        } else {
          this.index = this.index + 1;
        }
      } else {
        if (this.index - 1 < 0) {
          if (!loop) return;
          this.index = this.length - 1;
          wrapped = true;
        } else {
          this.index = this.index - 1;
        }
      }

      this.isTransitioning = true;
      this.checkSlide();

      this._sliderBlockScroll = true;
      this.classList.add("disable-snapping");

      const item = this.indexedItems[this.index];
      const left =
        this.dataset.slideAlign === "center"
          ? this.slideX(item) - (this.clientWidth - item.offsetWidth) / 2
          : this.slideX(item) - parseInt(getComputedStyle(this.indexedItems[0]).marginLeft);

      if (wrapped) {
        // Fade, jump, fade back in; reduced motion jumps straight there
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
          this.scrollTo({ top: 0, left, behavior: "auto" });
        } else {
          this.style.transition = "opacity 180ms ease";
          this.style.opacity = "0";
          setTimeout(() => {
            this.scrollTo({ top: 0, left, behavior: "auto" });
            this.style.opacity = "";
          }, 180);
        }
      } else {
        this.scrollTo({ top: 0, left, behavior: behavior });
      }

      // Wait for the scroll animation to fully complete before restoring
      // snap and unblocking. A fixed timeout can fire before the smooth
      // scroll finishes, causing snap to abort mid-animation and reset the
      // counter to the wrong slide.
      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        // Restore snap first, then unblock scroll events after a brief
        // pause so any snap micro-adjustment stays invisible to SCROLL_EVENT.
        this.classList.remove("disable-snapping");
        setTimeout(() => {
          this._sliderBlockScroll = false;
          this.isTransitioning = false;
        }, 50);
      };

      if ("onscrollend" in window) {
        this.addEventListener("scrollend", cleanup, { once: true });
        // Safety fallback in case scrollend never fires
        setTimeout(cleanup, 800);
      } else {
        setTimeout(cleanup, 600);
      }

      this.dispatchEvent(this._changeEvent);
    }

    // A slide's position inside this scroller. offsetLeft counts from the page wrapper, which past the 1440 cap is not here
    slideX(el) {
      return el.getBoundingClientRect().left - this.getBoundingClientRect().left + this.scrollLeft;
    }

    checkSlide() {
      // checks slide after index change and updates navigation / viewport
      const autoHeight = this.classList.contains("css-slider--auto-height");
      const h = autoHeight ? this.indexedItems[this.index].offsetHeight : null;

      // A looping slider never runs out, so neither arrow greys out
      const loop = this.hasAttribute("data-loop");
      this.prevEl.classList.toggle("disabled", !loop && this.index <= 0);
      this.nextEl.classList.toggle("disabled", !loop && this.index >= this.length - 1);

      this.indexEl.innerHTML = this.index + 1;
      if (h !== null) this.parentNode.style.height = `${h}px`;
    }

    afterAppend() {
      this.items = this.querySelectorAll(".css-slide[data-index]");
    }

    resetSlider(nojump = false, resetIndex = true) {
      let slidesWidth = 0,
        slidesPerPage = 0,
        page = 0,
        pages = 0,
        // The slider's own width, not the window's: past the 1440 cap they differ
        totalWidth = this.classList.contains("css-slider--auto-height")
          ? Math.min(window.innerWidth, this.clientWidth || window.innerWidth)
          : this.querySelector(".css-slides-container").offsetWidth - 20,
        hideNavigation = false;

      // 0 reset

      this.indexedItems = [];

      // read all widths before any DOM writes
      const itemWidths = Array.from(this.items, elm => elm.offsetWidth);

      this.classList.add("disable-snapping");

      // 1 find out how many pages

      this.items.forEach((elm, i) => {
        elm.classList.remove("css-slide--snap");
        slidesWidth += itemWidths[i];
        if (slidesWidth > totalWidth && slidesPerPage == 0) {
          slidesPerPage = i;
        }
      });

      if (slidesPerPage == 0) {
        slidesPerPage = this.items.length;
        hideNavigation = true;
      }

      this.items.forEach((elm, i) => {
        if (i % slidesPerPage == 0) {
          elm.classList.add("css-slide--snap");
          elm.setAttribute("data-index", page++);
        }
      });

      this.indexedItems = this.querySelectorAll(".css-slide--snap");

      if (resetIndex) {
        this.index = 0;
      }
      this.length = Math.ceil(this.items.length / slidesPerPage);
      this.lengthEl.innerHTML = this.length;
      this.indexEl.innerHTML = 1;

      if (hideNavigation) {
        this.navigation.style.display = "none";
        this.classList.add("css-slider--no-drag");
      } else {
        this.navigation.style.display = "block";
        this.classList.remove("css-slider--no-drag");
      }

      if (!nojump) {
        this.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      }
      this.classList.remove("disable-snapping");
    }
  }

  if (typeof customElements.get("css-slider") == "undefined") {
    customElements.define("css-slider", CSSSlider);
  }
}
